import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowRight, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('This reset link is missing its token. Please request a new password reset.');
    }
  }, [token]);

  const validate = () => {
    const er = {};
    if (!pwd) er.pwd = 'Please enter a new password';
    else if (pwd.length < 8) er.pwd = 'Password must be at least 8 characters';
    if (!confirm) er.confirm = 'Please confirm your new password';
    else if (confirm !== pwd) er.confirm = 'Passwords do not match';
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!token || !validate()) return;
    setStatus('loading');
    setMessage('');
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pwd });
      setStatus('success');
      setMessage('Your password has been updated. You can now sign in with your new password.');
    } catch (err) {
      setStatus('error');
      setMessage(err?.response?.data?.detail || 'Could not reset your password. Please try again.');
    }
  };

  return (
    <div className="bg-black min-h-screen">
      <Navbar />
      <main className="max-w-[560px] mx-auto px-6 py-20">
        <div className="rounded-2xl border border-neutral-800 bg-neutral-950 p-7">
          <p className="text-[12px] uppercase tracking-[0.18em] text-blue-400">MetaxuOne</p>
          <h1 className="mt-2 text-[26px] font-semibold tracking-tight text-white">Reset your password</h1>
          <p className="mt-2 text-[14px] text-neutral-400 leading-relaxed">Choose a new password for your MetaxuOne account.</p>

          {status === 'success' ? (
            <div className="mt-7 text-center">
              <div className="w-14 h-14 rounded-full bg-blue-500/15 border border-blue-500/40 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-blue-400" />
              </div>
              <p className="mt-5 text-[14px] text-neutral-300 leading-relaxed">{message}</p>
              <Button
                onClick={() => navigate('/')}
                className="mt-6 h-11 px-6 rounded-full bg-white text-neutral-950 hover:bg-neutral-200 text-[14px] font-medium"
              >
                Back to home
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="rp-pwd" className="text-[13px] text-neutral-300">New password</Label>
                <div className="relative">
                  <Input
                    id="rp-pwd"
                    type={showPwd ? 'text' : 'password'}
                    value={pwd}
                    onChange={(e) => { setPwd(e.target.value); if (errors.pwd) setErrors((er) => ({ ...er, pwd: undefined })); }}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11 pr-10"
                  />
                  <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute inset-y-0 right-0 px-3 text-neutral-500 hover:text-neutral-300" aria-label="Toggle password visibility">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.pwd && <p className="text-[12px] text-red-400">{errors.pwd}</p>}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="rp-confirm" className="text-[13px] text-neutral-300">Confirm new password</Label>
                <Input
                  id="rp-confirm"
                  type={showPwd ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => { setConfirm(e.target.value); if (errors.confirm) setErrors((er) => ({ ...er, confirm: undefined })); }}
                  placeholder="Repeat the new password"
                  autoComplete="new-password"
                  className="bg-neutral-900 border-neutral-800 text-white placeholder:text-neutral-600 focus-visible:ring-blue-500 focus-visible:border-blue-500 h-11"
                />
                {errors.confirm && <p className="text-[12px] text-red-400">{errors.confirm}</p>}
              </div>

              {status === 'error' && message && (
                <div className="flex items-start gap-2 rounded-md bg-red-500/10 border border-red-500/30 px-3 py-2.5">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <p className="text-[13px] text-red-300 leading-snug">{message}</p>
                </div>
              )}

              <Button
                type="submit"
                disabled={!token || status === 'loading'}
                className="w-full h-11 mt-1 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70"
              >
                {status === 'loading' ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>
                ) : (
                  <>Update password <ArrowRight className="w-4 h-4" /></>
                )}
              </Button>

              <p className="text-center text-[12.5px] text-neutral-500">
                <Link to="/" className="text-blue-400 hover:text-blue-300 underline">Back to home</Link>
              </p>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ResetPassword;
